import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchBranches } from "@/api/branches";
import { confirmStudentsImport, previewStudentsImport, type ImportPreview } from "@/api/students";
import { extractErrorMessage } from "@/types/common";

const DEFAULT_MAPPING = {
  fullName: "ФИО",
  phone: "Telefon",
  parentPhone: "Telefon otasi",
  birthYear: "Yosh",
  source: "Manba",
  status: "Holati",
};

export function StudentsImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const client = useQueryClient();
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const [file, setFile] = useState<File>();
  const [branchId, setBranchId] = useState("");
  const [mapping, setMapping] = useState(DEFAULT_MAPPING);
  const [preview, setPreview] = useState<ImportPreview>();
  const [error, setError] = useState<string>();
  const previewMutation = useMutation({
    mutationFn: () => previewStudentsImport(file!, branchId, mapping),
    onSuccess: setPreview,
    onError: (reason) => setError(extractErrorMessage(reason)),
  });
  const confirmMutation = useMutation({
    mutationFn: () => confirmStudentsImport(preview!.importRunId, branchId),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ["students"] }); onClose(); },
    onError: (reason) => setError(extractErrorMessage(reason)),
  });

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle>Импорт учеников из CSV/XLSX</DialogTitle>
    <DialogContent>
      <Stack spacing={2} mt={1}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField select label="Филиал" value={branchId} onChange={(event) => setBranchId(event.target.value)}>
          {(branches ?? []).map((branch) => <MenuItem key={branch.id} value={branch.id}>{branch.name}</MenuItem>)}
        </TextField>
        <Button component="label" variant="outlined">Выбрать CSV/XLSX<input hidden type="file" accept=".csv,.xlsx" onChange={(event) => { setFile(event.target.files?.[0]); setPreview(undefined); }} /></Button>
        {file && <Alert severity="info">Файл: {file.name}</Alert>}
        <Alert severity="info">Укажите точные названия колонок из первой строки файла.</Alert>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          {Object.entries(mapping).map(([field, header]) => <TextField key={field} size="small" label={field} value={header} onChange={(event) => setMapping({ ...mapping, [field]: event.target.value })} />)}
        </Stack>
        {preview && <Alert severity={preview.invalidRows ? "warning" : "success"}>
          Строк: {preview.totalRows}; готовы: {preview.validRows}; ошибок: {preview.invalidRows}.
          {preview.errors.map((issue) => <div key={`${issue.row}-${issue.message}`}>Строка {issue.row}: {issue.message}</div>)}
        </Alert>}
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Отмена</Button>
      {!preview && <Button disabled={!file || !branchId || previewMutation.isPending} variant="contained" onClick={() => previewMutation.mutate()}>Проверить файл</Button>}
      {preview && <Button disabled={preview.invalidRows > 0 || confirmMutation.isPending} variant="contained" onClick={() => confirmMutation.mutate()}>Подтвердить импорт</Button>}
    </DialogActions>
  </Dialog>;
}
